import { translateStatusCode } from '../../http-status-codes-helper';
import apiLoggiWeb from './loggiweb';

function looksLikeHtml(value) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return /^<!DOCTYPE\s+html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed);
}

function extractProductMessage(errorObject = {}) {
  if (typeof errorObject.message === 'string') return errorObject.message;
  if (typeof errorObject.detail === 'string') return errorObject.detail;
  const firstError = errorObject.errors?.[0];
  if (typeof firstError === 'string') return firstError;
  if (typeof firstError?.message === 'string') return firstError.message;
  return '';
}

function isServerErrorStatus(status) {
  return typeof status === 'number' && status >= 500 && status <= 599;
}

function hasProductJsonPayload(json) {
  return (
    !!json &&
    typeof json === 'object' &&
    !Array.isArray(json) &&
    !!(json.message || json.detail || json.errors)
  );
}

function shouldUseStatusMappedMessage(error, json, message) {
  return (
    looksLikeHtml(message) ||
    looksLikeHtml(error.text) ||
    looksLikeHtml(error.message) ||
    (isServerErrorStatus(error.status) && !hasProductJsonPayload(json))
  );
}

export function parserErrorFromApi(error = {}) {
  let { json } = error;

  if (json === undefined && typeof error.message === 'string') {
    try {
      json = JSON.parse(error.message);
    } catch {
      // Body is plain text (often an HTML gateway page).
    }
  }

  let errorObject;

  if (typeof json === 'string') {
    /**
     * When an exception is thrown in backend, it will arrive as a string
     * for example, in python: raise ValidationError(str(e))
     */
    errorObject = { message: json };
  } else if (Array.isArray(json) && typeof json[0] === 'string') {
    /**
     * When an exception is thrown in backend, it will arrive as a string or as an array
     * and the first item as a string
     * for example, in python: raise InvalidPackageUpdateStatusRequest(
     *   "Invalid request as package_id or status is missing or an empty data was passed"
     * )
     * 0: "Invalid request as package_id or status is missing or an empty data was passed"
     */
    errorObject = { message: json[0] };
  } else if (json?.message) {
    errorObject = json;
  } else if (json?.detail) {
    /**
     * There is a class validation on authentication parameters that throws api error with a
     * detail field... we use the "message" field in the front end
     */
    errorObject = { ...json, message: json.detail };
  } else if (json?.errors) {
    errorObject = json;
  } else {
    errorObject = { message: error.message || error.text || '' };
  }

  let message = extractProductMessage(errorObject);

  if (shouldUseStatusMappedMessage(error, json, message)) {
    message = translateStatusCode(error.status);
    errorObject = { ...errorObject, message };
  }

  return {
    message,
    response: {
      ...errorObject,
      message,
      status: error.status,
      raw: JSON.stringify(error)
    }
  };
}

function rejectParsedApiError(err) {
  if (err.status) {
    throw parserErrorFromApi(err);
  }
  throw err;
}

/*
 * This object is created and exported to maintain compatibility with the unused
 * axios library and is a part of the transition period from axios to wretch.
 *
 * @deprecated
 */
export default {
  get: endpoint =>
    apiLoggiWeb()
      .url(endpoint)
      .get()
      .fetchError(err => {
        const message = err?.message || '';

        // eslint-disable-next-line no-throw-literal
        throw new Error(message, {
          isFetchError: true
        });
      })
      .json()
      .catch(rejectParsedApiError),
  patch: (endpoint, data) =>
    apiLoggiWeb()
      .url(endpoint)
      .patch(data)
      .fetchError(err => {
        const message = err?.message || '';

        // eslint-disable-next-line no-throw-literal
        throw new Error(message, {
          isFetchError: true,
          ...err
        });
      })
      .json()
      .catch(rejectParsedApiError),
  post: (endpoint, object) =>
    apiLoggiWeb()
      .url(endpoint)
      .post(object)
      .fetchError(err => {
        const message = err?.message || '';

        // eslint-disable-next-line no-throw-literal
        throw new Error(message, {
          isFetchError: true,
          ...err
        });
      })
      .json()
      .catch(rejectParsedApiError)
};
